import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Borrower } from '../../models/borrower-model';
import { ResidencyBasis } from '../../models/enums/residency-basis.enum';
import { ResidencyType } from '../../models/enums/residency-type.enum';
import { ResidencyAddress } from '../../models/residency-address.model';
import { ConfigurableFieldHostComponent } from '../configurable-field-host.component';
import { AddressHistory2Component } from '../wizard-steps/address-history2/address-history2.component';
import { ReoPropertiesComponent } from '../wizard-steps/reo-properties/reo-properties.component';
import { formViewProvider } from '../../services/form-view.provider';
import { MortgageApplication } from '../../models/mortgage-app.model';
import { EnumsService } from '../../services/enums.service';
import { Address } from '../../models/address-model';
import { RealEstateOwned } from '../../models/mortgage.model';
import { reoMatchesAddress, normalizeAddressPart } from '../reo-address-match.util';
import { isActive, removeOrMarkDeleted, undoDelete } from '../../utils/entity-state';
import { relinkReoLiens, unlinkReoLiens } from '../wizard-steps/reo-liens';

@Component({
    selector: 'borrower-address-history',
    templateUrl: 'borrower-address-history.component.html',
    viewProviders: [formViewProvider],
    standalone: false
})
export class BorrowerAddressHistoryComponent extends ConfigurableFieldHostComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @ViewChildren('previousAddressHistory') addressHistoryComponents: QueryList<AddressHistory2Component> | undefined;

  @ViewChild('currentAddressHistory') currentAddressComponent: AddressHistory2Component | undefined;

  @ViewChild("reoProperties")
  reoProperties: ReoPropertiesComponent;

  @ViewChild('addressHistoryForm') addressHistoryForm: NgForm | undefined;

  @Input()
  mortgage: MortgageApplication;

  @Input()
  borrower!: Borrower;

  @Input()
  currentAddressLabel!: string;

  @Input()
  reoFieldsToConfig: any = {};

  @Input()
  inEditMode: boolean = false;

  @Output()
  residencyBasedChangedToNonOwner: EventEmitter<any> = new EventEmitter<any>();

  currentAddress!: ResidencyAddress;
  previousAddresses: ResidencyAddress[] = [];

  currentAddressFieldsToConfig: any = {};

  monthsOfAddressHistory: number = 0;

  previousAddressToDelete: ResidencyAddress | undefined = undefined;

  showInsufficientAddressHistoryWarning: boolean = false;

  protected reoForCurrentAddress: RealEstateOwned = new RealEstateOwned();

  private _invalidComponent;

  private _invalidComponentName: string;;

  // LLS-569: liens unlinked by addOrRemoveReoBasedOnOwnershipStatus, keyed by reoId, so an
  // Own -> Rent -> Own round trip within the same session re-links them onto the revived REO
  // instead of leaving them orphaned forever.
  private _unlinkedLienIdsByReo = new Map<number, number[]>();

  constructor(private readonly _enumsService: EnumsService) {
    super();
  }

  public get hasAtLeast2YearsOfAddressHistory(): boolean {
    const residencyAddresses = [...this.previousAddresses, this.currentAddress];
    this.monthsOfAddressHistory = this.calculateMonthsOfAddressHistory(residencyAddresses);
    return this.monthsOfAddressHistory >= 24;
  }

  ngOnInit() {
    this.initializeAddresses();
    this.recomputeCurrentAddressFieldsToConfig();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['borrower'] && !changes['borrower'].firstChange) {
      this.initializeAddresses();
    }
    if (changes['fieldsToConfig'] || changes['reoFieldsToConfig']) {
      this.recomputeCurrentAddressFieldsToConfig();
    }
  }

  private recomputeCurrentAddressFieldsToConfig = () => {
    // Address-history2 for the current address renders both residency-level fields
    // (residencyAddress.*) and the inline REO Date Acquired field (reo.dateAcquired).
    // Merge the two namespaces so its isHidden/isRequired/getLabel calls resolve
    // against a single config object.
    this.currentAddressFieldsToConfig = { ...this.fieldConfig, ...this.reoFieldsToConfig };
  }

  public initializeAddresses() {
    this.currentAddress = this.borrower.residencyAddresses.find(a => a.residencyType == ResidencyType.PresentAddress);
    if (!this.currentAddress) {
      this.currentAddress = new ResidencyAddress(ResidencyType.PresentAddress);
      this.borrower.residencyAddresses.push(this.currentAddress);
    }
    this.previousAddresses = this.borrower.residencyAddresses.filter(a => a.residencyType == ResidencyType.FormerAddress);

    // Reinitialize REO data for the current address (needed when copying from another borrower)
    this.reoForCurrentAddress = this.initializeReoRelatedToAddress(this.currentAddress);
    this.addOrRemoveReoBasedOnOwnershipStatus(this.currentAddress.residencyBasis);
  }

  ngAfterViewInit() {
    this.reoForCurrentAddress = this.initializeReoRelatedToAddress(this.currentAddress);
    this.addOrRemoveReoBasedOnOwnershipStatus(this.currentAddress.residencyBasis);
  }

  ngOnDestroy(): void {
  }

  private initializeReoRelatedToAddress = (address: ResidencyAddress): RealEstateOwned => {
    if (!this.inEditMode) {
      const existingReo = this.findAlreadyExistingReoInMortgageByAddress(address.address);
      if (existingReo) {
        this.fixReoAcquiredDate(existingReo);
        return existingReo;
      } else {
        const reo = new RealEstateOwned();
        reo.address1 = address.address?.address1;
        reo.city = address.address?.city;
        reo.state = address.address?.state;
        reo.zipCode = address.address?.zipCode;
        reo.owningBorrowerIds = [this.borrower.borrowerId];
        return reo;
      }
    } else {
      return this.initializeReoDefaultsForEditMode();
    }
  }

  onAddressChanged = (address: Address) => {
    const index = this.reoForCurrentAddress.owningBorrowerIds.findIndex(id => id == this.borrower.borrowerId);
    if (index >= 0) {
      this.reoForCurrentAddress.owningBorrowerIds.splice(index, 1);
    }
    this.reoForCurrentAddress = this.initializeReoRelatedToAddress(this.currentAddress);
    this.addOrRemoveReoBasedOnOwnershipStatus(this.currentAddress.residencyBasis);
  }

  onOwnershipStatusChanged = (ownershipStatus: ResidencyBasis) => {
    this.addOrRemoveReoBasedOnOwnershipStatus(ownershipStatus);
  }

  onCurrentAddressDateAcquiredChanged = () => {
    if (this.mortgage?.subjectProperty?.purposeOfLoan === 'Refinance' && this.reoForCurrentAddress?.isSubjectProperty) {
      const year = this.reoForCurrentAddress.dateAcquired ? new Date(this.reoForCurrentAddress.dateAcquired).getFullYear() : undefined;
      this.mortgage.subjectProperty.originalCostYear = year;
    }
  }

  onPreviousAddressDeleteCancelClicked = (previousAddress: ResidencyAddress) => {
    this.previousAddressToDelete = undefined;
  }

  onPreviousAddressDeleteConfirmClicked = (previousAddress: ResidencyAddress) => {
    const index = this.borrower.residencyAddresses.indexOf(previousAddress);
    if (index >= 0) {
      this.borrower.residencyAddresses.splice(index, 1);
      this.previousAddresses = this.borrower.residencyAddresses.filter(a => a.residencyType == ResidencyType.FormerAddress);
    }
    this.previousAddressToDelete = undefined;
  }

  private addPreviousAddressClicked = () => {
    const previousAddress = new ResidencyAddress(ResidencyType.FormerAddress);
    this.borrower.residencyAddresses.push(previousAddress);
    this.previousAddresses = this.borrower.residencyAddresses.filter(a => a.residencyType == ResidencyType.FormerAddress);
  }

  onPreviousAddressDeleteClicked = (previousAddress: ResidencyAddress) => {
    this.previousAddressToDelete = previousAddress;
  }

  onDurationAtAddressChanged = (e: any) => {
    // const hasEnoughAddressHistory = this.hasAtLeast2YearsOfAddressHistory;
    // this.showInsufficientAddressHistoryWarning = !hasEnoughAddressHistory;
    // if (!hasEnoughAddressHistory) {
    //   if (this.validateAddressHistoryFields()) {
    //     this.addPreviousAddressClicked();
    //   }
    // }
  }

  public validateAddressHistory = (): boolean => {
    const valid = this.validateAddressHistoryFields();
    const hasAtLeast2YearsOfAddressHistory = this.hasAtLeast2YearsOfAddressHistory;
    if (!hasAtLeast2YearsOfAddressHistory && valid) {
      this.addPreviousAddressClicked();
    }
    return valid && hasAtLeast2YearsOfAddressHistory;
  }

  private addOrRemoveReoBasedOnOwnershipStatus = (ownershipStatus: ResidencyBasis) => {
    const alreadyExistingReo = this.findAlreadyExistingReoInMortgageByAddress(this.currentAddress.address);
    if (ownershipStatus === ResidencyBasis.Own) {
      if (!alreadyExistingReo) {
        // LLS-140: only track an REO once the property is identifiable. Pushing a blank-street
        // REO here is unrecoverable — the normalized matcher (correctly) never re-finds a blank
        // address, so re-init / ownership-toggle would otherwise pile up phantom blank REOs.
        if (normalizeAddressPart(this.currentAddress.address?.address1)) {
          // Toggling back to "own" revives the row we marked deleted rather than
          // queueing a delete and an insert for the same property.
          undoDelete(this.reoForCurrentAddress);
          if (!this.mortgage.realEstateOwned.includes(this.reoForCurrentAddress)) {
            this.mortgage.realEstateOwned.push(this.reoForCurrentAddress);
          }
          // LLS-569: re-link any liens this same REO lost earlier in the session.
          if (this.reoForCurrentAddress.reoId != null) {
            const unlinkedIds = this._unlinkedLienIdsByReo.get(this.reoForCurrentAddress.reoId);
            if (unlinkedIds) {
              relinkReoLiens(this.mortgage, this.reoForCurrentAddress, unlinkedIds);
              this._unlinkedLienIdsByReo.delete(this.reoForCurrentAddress.reoId);
            }
          }
        }
      } else {
        const index = alreadyExistingReo.owningBorrowerIds.findIndex(id => id == this.borrower.borrowerId);
        if (index < 0) {
          alreadyExistingReo.owningBorrowerIds.push(this.borrower.borrowerId);
        }
      }
    } else {
      if (alreadyExistingReo) {
        if (alreadyExistingReo.owningBorrowerIds.includes(this.borrower.borrowerId)) {
          if (alreadyExistingReo.owningBorrowerIds.length > 1) {
            const index = alreadyExistingReo.owningBorrowerIds.findIndex(id => id == this.borrower.borrowerId);
            if (index >= 0) {
              alreadyExistingReo.owningBorrowerIds.splice(index, 1);
            }
            if (alreadyExistingReo.owningBorrowerIds.length === 0) {
              removeOrMarkDeleted(this.mortgage.realEstateOwned, alreadyExistingReo, alreadyExistingReo.reoId);
              // LLS-569: mirror reo-step's REO delete - liens the sale/edit flow put top-level on
              // the mortgage must stop pointing at an REO that no longer exists.
              const unlinkedIds = unlinkReoLiens(this.mortgage, alreadyExistingReo);
              if (alreadyExistingReo.reoId != null) {
                this._unlinkedLienIdsByReo.set(alreadyExistingReo.reoId, unlinkedIds);
              }
            }
          } else {
            removeOrMarkDeleted(this.mortgage.realEstateOwned, this.reoForCurrentAddress, this.reoForCurrentAddress?.reoId);
            const unlinkedIds = unlinkReoLiens(this.mortgage, this.reoForCurrentAddress);
            if (this.reoForCurrentAddress.reoId != null) {
              this._unlinkedLienIdsByReo.set(this.reoForCurrentAddress.reoId, unlinkedIds);
            }
          }
        }
      }
    }
  }

  private calculateMonthsOfAddressHistory = (residencyAddresses: ResidencyAddress[]): number => {
    let months: number = 0;
    for (const residencyAddress of residencyAddresses) {
      const yearsAtAddress = residencyAddress.durationYears ? residencyAddress.durationYears : 0;
      const monthsAtAddress = residencyAddress.durationMonths ? residencyAddress.durationMonths : 0;
      months = months + 12 * yearsAtAddress + monthsAtAddress;
    }
    return months;
  }

  private validateAddressHistoryFields = (): boolean => {
    let valid = true;
    if (this.addressHistoryForm) {
      this.addressHistoryForm.form.markAllAsTouched();
      valid = this.addressHistoryForm.form.valid;
    }
    if (this.currentAddressComponent) {
      valid = this.currentAddressComponent.validate() && valid;
      if (!valid) {
        this._invalidComponentName = 'currentAddress';
      }
    }
    if (this.addressHistoryComponents) {
      const addressHistoryComponents: AddressHistory2Component[] = this.addressHistoryComponents.toArray();
      addressHistoryComponents.forEach(c => {
        valid = c.validate() && valid;
        if (!valid && !this._invalidComponentName && !this._invalidComponent)
          this._invalidComponentName = 'previousAddresses';
        this._invalidComponent = c;
      })
    }
    return valid;
  }

  public validate = (): boolean => {
    this._invalidComponentName = null;
    this._invalidComponent = null;
    let valid = this.validateAddressHistoryFields();
    if (!valid) {
      return false;
    }
    // LLS-140: a street typed manually (no autocomplete selection) may not have fired
    // onAddressChanged, leaving reoForCurrentAddress's address stale and — with the blank-street
    // push guard — untracked. Now that the address has validated (non-blank), sync it onto the
    // working REO (preserving any entered financials, since it is the same object) and ensure it
    // is tracked exactly once before the step saves. addOrRemove is idempotent, so this is safe.
    if (this.currentAddress.residencyBasis === ResidencyBasis.Own && this.reoForCurrentAddress) {
      const addr = this.currentAddress.address;
      this.reoForCurrentAddress.address1 = addr?.address1;
      this.reoForCurrentAddress.city = addr?.city;
      this.reoForCurrentAddress.state = addr?.state;
      this.reoForCurrentAddress.zipCode = addr?.zipCode;
      this.addOrRemoveReoBasedOnOwnershipStatus(ResidencyBasis.Own);
    }
    if (this.currentAddress.residencyBasis === 'Own') {
      valid = this.reoProperties.validate();
      if (!valid) {
        this._invalidComponentName = 'reo';
      }
    }
    if (valid) {
      const hasEnoughAddressHistory = this.hasAtLeast2YearsOfAddressHistory;
      this.showInsufficientAddressHistoryWarning = !hasEnoughAddressHistory;
      if (!hasEnoughAddressHistory) {
        this.addPreviousAddressClicked();
      }
      valid = hasEnoughAddressHistory;
    }

    return valid;
  }

  public scrollToFirstInvalid = () => {
    let firstInvalidOneId: string = null;
    switch (this._invalidComponentName) {
      case 'currentAddress': {
        const addressFormControls = Object.assign(
          this.currentAddressComponent.addressComponent.addressForm.form.controls,
          this.currentAddressComponent.addressRelatedInfoForm.form.controls
        );

        for (const key in addressFormControls) {
          if (Object.prototype.hasOwnProperty.call(addressFormControls, key)) {
            if (addressFormControls[key].status === 'INVALID') {
              firstInvalidOneId = key;

              break;
            }
          }
        }
        break;
      }
      case 'reo':
        for (const key in this.reoProperties.reoPropertiesForm.form.controls) {
          if (Object.prototype.hasOwnProperty.call(this.reoProperties.reoPropertiesForm.form.controls, key)) {
            if (this.reoProperties.reoPropertiesForm.form.controls[key].status === 'INVALID') {
              firstInvalidOneId = key;
              break;
            }
          }
        }
        break;
      case 'previousAddresses': {
        const previousAddressFormControls = Object.assign(
          this._invalidComponent.addressComponent.addressForm.form.controls,
          this._invalidComponent.addressRelatedInfoForm.form.controls
        );
        for (const key in previousAddressFormControls) {
          if (Object.prototype.hasOwnProperty.call(previousAddressFormControls, key)) {
            if (previousAddressFormControls[key].status === 'INVALID') {
              firstInvalidOneId = key;
              break;
            }
          }
        }
        break;
      }
    }
    if (firstInvalidOneId) {
      const element = document.getElementById(firstInvalidOneId);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }, 250);
      }
    }
  }

  private fixReoAcquiredDate = (reo: RealEstateOwned) => {
    if (reo.dateAcquired) {
      const dateAcquired = new Date(reo.dateAcquired);
      let monthString = "";
      const month = dateAcquired.getMonth() + 1;
      monthString = month >= 10 ? month.toLocaleString() : "0" + month;
      let dayString = "";
      const day = dateAcquired.getDate();
      dayString = day >= 10 ? day.toLocaleString() : "0" + day;
      reo.dateAcquired = monthString + '/' + dayString + '/' + dateAcquired.getFullYear();
    }
  }

  private initializeReoDefaultsForEditMode = (): RealEstateOwned => {
    const reo = new RealEstateOwned();
    reo.dateAcquired = '01/01/2000';
    reo.currentUsageType = this._enumsService.usageTypes[0].value;
    reo.intendedUsageType = this._enumsService.usageTypes[0].value;
    reo.typeOfProperty = this._enumsService.reoPropertyTypes[0].value;
    reo.marketValue = 500000;
    reo.purchasePrice = 450000;
    reo.dispositionStatus = this._enumsService.reoStatusTypes[0].value;
    reo.amountOfMortgage = 100000;
    reo.mortgagePayment = 3450;
    reo.monthlyMiscExpenses = 5600;
    return reo;
  }

  private findAlreadyExistingReoInMortgageByAddress = (address: Address): RealEstateOwned => {
    // LLS-140: normalized property match (see reo-address-match.util) so an owned address is
    // reconciled to its existing REO rather than pushing a duplicate when formatting differs.
    return this.mortgage?.realEstateOwned.find(reo => isActive(reo) && reoMatchesAddress(reo, address));
  }
}
